import { FAIcon } from '~/components/utils/FAIcons';
import { MutationButton } from '~/components/utils/buttons';
import { ErrorMessage } from './ErrorMessage';
import type { FormEventHandler } from 'react';

type SingleInputForm = {
	autoFocus?: boolean;
	border?: boolean;
	containerClassName?: string;
	displayMutationButtons?: boolean;
	errorMessage?: string;
	id: string;
	inputClassName?: string;
	label?: string;
	mutation: any;
	onCancel: () => void;
	onSubmit: FormEventHandler<HTMLFormElement> | undefined;
	padding?: string;
	rows?: number;
	type?: string;
	valueAsNumber?: boolean;
} & (
	| {
			children: React.ReactNode;
			name?: undefined;
			register?: undefined;
	  }
	| {
			children?: undefined;
			name: string;
			register: any;
	  }
);

export const SingleInputForm = ({
	autoFocus = true,
	border = true,
	children,
	containerClassName,
	displayMutationButtons = true,
	errorMessage,
	id,
	inputClassName,
	label,
	mutation,
	name,
	onCancel,
	onSubmit,
	padding,
	register,
	rows,
	type = 'TEXT',
	valueAsNumber,
}: SingleInputForm) => {
	const InputFromType = () => {
		switch (type) {
			case 'TEXT':
				return (
					<input
						id={`${id}-input`}
						{...register(name, {
							valueAsNumber: valueAsNumber ? valueAsNumber : false,
						})}
						name={name}
						className={`${padding ? padding : 'py-2 px-2'} w-full focus:outline-hidden ${inputClassName}`}
						autoFocus={autoFocus}
					/>
				);
			case 'TEXT_AREA':
				return (
					<textarea
						{...register(name)}
						id={`${id}-input`}
						className={`${padding ? padding : 'py-2 px-2'} w-full focus:outline-hidden ${inputClassName}`}
						name={name}
						rows={rows ? rows : 4}
						autoFocus={autoFocus}
						autoComplete="off"
					/>
				);
			default:
				return (
					<input
						id={`${id}-input`}
						{...register(name, {
							valueAsNumber: valueAsNumber ? valueAsNumber : false,
						})}
						name={name}
						className={`${padding ? padding : 'py-2 px-2'} w-full focus:outline-hidden ${inputClassName}`}
						autoFocus={autoFocus}
					/>
				);
		}
	};

	return (
		<>
			<form
				id={id}
				autoComplete="off"
				onSubmit={onSubmit}
				className={`flex items-center ${border ? 'border-b border-gray-400' : ''} justify-between ${containerClassName}`}
			>
				<div className={`grow flex items-end`}>
					{label && <label htmlFor={`${id}-input`}>{label}</label>}

					{children ?? <InputFromType />}
				</div>
				{displayMutationButtons && (
					<div className="shadow-sm whitespace-nowrap">
						<MutationButton
							id={`${id}-submit`}
							className="text-green-600 hover:bg-green-600 hover:text-white hover:border-green-600 border  border-gray-400 border-r-0 rounded-l-lg h-7 w-7"
							disableOnSuccess={false}
							mutation={mutation}
							showLoadingText={false}
						>
							<FAIcon icon="check" />
						</MutationButton>
						<button
							type="button"
							onClick={() => onCancel()}
							className="text-red-500 hover:bg-red-500 hover:border-red-500 hover:text-white border border-gray-400 border-l-0 h-7 w-7 rounded-r-lg"
						>
							<FAIcon icon="times" />
						</button>
					</div>
				)}
			</form>
			{errorMessage && (
				<div className="w-[200px] text-sm">
					<ErrorMessage id={`${id}-input-error`} errorMessage={errorMessage} />
				</div>
			)}
		</>
	);
};
